import * as React from 'react';
import { Transition } from 'react-transition-group';
import Styled, { css } from 'styled-components';

import { media } from '../../../lib/StyledComponents';
import { Label } from '../../Typography';
import { SVGIcon } from '../../Icon';
import { ScreenReaderOnly, withTrapFocus } from '../../Accessibility';
import { VALID_TRANSITION_STATES } from '../../_ThemeTwo_/Animations/Animations';

import { MenuGroup } from './MenuGroup';
import { MenuItem } from './MenuItem';
import MenuToggle, { IMenuToggleProps } from './MenuToggle';

const TRANSITION_DURATION: number = 100;

const MenuClose = Styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  padding: 1em 1.375em;
  top: 0.75rem;
  right: 1.75rem;
  color: ${({ theme: { colorWhite } }) => colorWhite };

  ${media.small`
    top: 1.5rem;
  `}

  svg {
    width: 1em;
    height: 1em;
    fill: ${({ theme: { colorWhite } }) => colorWhite};
    transition: fill 0.2s ease;
  }

  &:hover {
    svg {
      fill: ${({ theme }) => theme.colorSlate20};
    }
  }

  div {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const MenuHeader = Styled(Label)
.attrs({ as: 'h1' })`
  display: block;
  color: ${({ theme }) => theme.colorWhite};
  font-size: 1.4rem;
  padding-bottom: 1.5em;
  margin: 4em 0 0;
`;

export const MenuContainer = Styled.div<{
  showMenu: boolean;
  transitionState: VALID_TRANSITION_STATES;
  toggleMenu (): void;
 }>`
  position: fixed;
  top: 0;
  right: 0;
  min-height: 100vh;
  max-height: 100%;
  width: 100%;
  z-index: 10001;
  background: #022a3a;
  padding: 2em 3.25em;
  transform:
    ${({ transitionState }) => {
      switch (transitionState) {
        case VALID_TRANSITION_STATES.entered:
          return 'translateX(0)';
        case VALID_TRANSITION_STATES.entering:
          return 'translateX(100%)';
        case VALID_TRANSITION_STATES.exiting:
          return 'translateX(100%)';
        default:
          return 'translateX(100%)';
    }
  }};
  transition: transform ${TRANSITION_DURATION}ms ease, box-shadow ${TRANSITION_DURATION}ms ease-out;
  pointer-events: ${({ showMenu }) => showMenu ? 'initial' : 'none'};
  overflow-y: scroll;
  box-shadow: ${({ showMenu }) => showMenu ? '0 16px 48px 0 rgba(0, 0, 0, 0.3)' : 'none'};
  /* stylelint-disable-next-line selector-type-no-unknown */
  ${MenuGroup}:first-of-type {
    padding-top: 0;
  }

  ${({ transitionState }) => css`
    ${media.small`
      width: 22.5em;
      transform:
        ${(() => {
          switch (transitionState) {
            case VALID_TRANSITION_STATES.entered:
              return 'translateX(0)';
            case VALID_TRANSITION_STATES.entering:
              return 'translateX(22.5em)';
            case VALID_TRANSITION_STATES.exiting:
              return 'translateX(22.5em)';
            default:
              return 'translateX(22.5em)';
          }
        })()};
    `}
  `}
`;

interface IMenuProps {
  showMenu: boolean;
  menuHeadingText?: string;
  onClick (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>): void;
}

const TrapContainer = withTrapFocus(MenuContainer);
// Transition component docs: http://reactcommunity.org/react-transition-group/transition
const Menu: React.FC<IMenuProps> &
  { Item: React.FC; Group: React.FC; Toggle: React.FC<IMenuToggleProps> } =
  ({ showMenu, children, onClick, menuHeadingText }) => (
      <Transition
        in={showMenu}
        timeout={{
          appear: 0,
          enter: 0,
          exit: TRANSITION_DURATION,
         }}
      >
        {(state: VALID_TRANSITION_STATES) => (
            state === VALID_TRANSITION_STATES.exited ? null : (
              <TrapContainer
                showMenu={showMenu}
                data-menu={true}
                id='menu-control'
                toggleMenu={onClick}
                transitionState={state}
                role='dialog'
                aria-modal='true'
                aria-label='menu'
              >
              <MenuClose data-event='MenuClose' onClick={onClick}>
                <SVGIcon icon='close' />
                <ScreenReaderOnly>Close Menu</ScreenReaderOnly>
              </MenuClose>
              {menuHeadingText ?
                <MenuHeader>{menuHeadingText}</MenuHeader> :
                <ScreenReaderOnly><MenuHeader>Menu</MenuHeader></ScreenReaderOnly>
              }
                {children}
              </TrapContainer>
            )
          )}
      </Transition>
    );

Menu.Item = MenuItem;
Menu.Group = MenuGroup;
Menu.Toggle = MenuToggle;

export default Menu;
